import { CommunitySearch, Geogroup, HttpService } from '@geovelo-frontends/commons';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, navigate } from 'gatsby';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';
import Dialog from '../../components/dialog';
import Seo from '../../components/seo';
import Banner from '../../components/slices/banner';
import { maxWidth } from '../../components/slices/consts';
import { AppContext } from '../../context';
import environment from '../../environment';
import { TPageProps } from '../../page-props';
import Breadcrumb from '../communities/breadcrumb';
import CompanyMigrationDialog from '../communities/company-migration-dialog';

function CommunitiesSearchPage(props: TPageProps) {
  const [initialized, setInitialized] = useState(false);
  const [companyMigrationDialogOpen, openCompanyMigrationDialog] = useState(false);
  const [companyToMigrate, setCompanyToMigrate] = useState<Geogroup>();
  const {
    user: { current: currentUser, geogroups: userGeogroups },
    actions: { getUserGeogroups },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { transitions } = useTheme();

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized && currentUser && !userGeogroups) getUserGeogroups();
  }, [initialized, currentUser]);

  const title = t('geovelo.pages_titles.community_search');

  return (
    <>
      <Seo title={title} {...props} />
      <Banner
        small
        data={{
          id: '',
          slice_type: 'banner',
          primary: {
            fullscreen_background: true,
            background_color_start: '#326AC2',
            dark_background: true,
            baseline: { text: title },
          },
        }}
      />
      <Box
        display="flex"
        flexDirection="column"
        gap={{ xs: 6, lg: 8 }}
        minHeight="calc(100svh - 272px)"
        paddingBottom={{ xs: 6, lg: 8 }}
        paddingTop={{ xs: 6, lg: 8 }}
      >
        <Box
          alignItems="center"
          alignSelf="stretch"
          display="flex"
          flexDirection="column"
          flexGrow={1}
        >
          <Box
            display="flex"
            flexDirection="column"
            flexGrow={1}
            gap={5}
            maxWidth="100%"
            paddingX={{ xs: 4, md: 8, lg: 16 }}
            width={maxWidth}
          >
            <Breadcrumb dark geogroup={null} items={[{ label: title }]} />
            <CommunitySearch
              Button={Button}
              createCommunityCardActions={
                <>
                  <Button
                    color="primary"
                    component={Link}
                    size="medium"
                    state={
                      currentUser ? {} : { prevPath: `/${currentLanguage}/communities/create` }
                    }
                    to={
                      currentUser
                        ? `/${currentLanguage}/communities/create`
                        : `/${currentLanguage}/sign-in`
                    }
                    variant="contained"
                  >
                    {t('commons.communities.form.actions.create')}
                  </Button>
                </>
              }
              currentUser={currentUser}
              Dialog={Dialog}
              handleClick={(geogroup) => {
                if (geogroup.type === 'company') {
                  if (geogroup.partner) {
                    window.open(
                      `${environment.enterpriseUrl}?auth-token=${
                        HttpService.authorizationToken?.substring(
                          HttpService.authorizationToken.indexOf(' ') + 1,
                        ) || ''
                      }&user-id=${currentUser?.id || ''}&partner-id=${geogroup.partner.id}`,
                      '_blank',
                    );
                  } else {
                    setCompanyToMigrate(geogroup);
                    openCompanyMigrationDialog(true);
                  }
                } else {
                  navigate(`/${currentLanguage}/communities/${geogroup.id}`);
                }
              }}
              handleCommunityJoined={async (id) => {
                await getUserGeogroups();
                navigate(`/${currentLanguage}/communities/${id}`);
              }}
              handleTryToJoinAsGuest={() =>
                navigate(`/${currentLanguage}/sign-in`, {
                  state: { prevPath: `/${currentLanguage}/communities/search` },
                })
              }
              userGeogroups={userGeogroups}
            />
          </Box>
        </Box>
      </Box>
      <CompanyMigrationDialog
        geogroup={companyToMigrate}
        onClose={() => {
          openCompanyMigrationDialog(false);
          setTimeout(() => setCompanyToMigrate(undefined), transitions.duration.leavingScreen);
        }}
        open={companyMigrationDialogOpen}
      />
    </>
  );
}

export default CommunitiesSearchPage;
